/**
 * Serbian grammar helpers. Serbian has three plural forms and getting them wrong is the fastest
 * way to make a product feel machine-translated, so every count that reaches a student goes
 * through here.
 *
 *   1, 21, 101    -> one   ("1 rad")
 *   2-4, 22-24    -> few   ("2 rada")
 *   0, 5-20, 25+  -> many  ("5 radova")
 */

export type PluralForms = readonly [one: string, few: string, many: string];

export function pluralForm(n: number): 0 | 1 | 2 {
  const abs = Math.abs(Math.trunc(n));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return 0;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 1;
  return 2;
}

export function plural(n: number, forms: PluralForms): string {
  return forms[pluralForm(n)];
}

export function counted(n: number, forms: PluralForms): string {
  return `${n} ${plural(n, forms)}`;
}

export const WORDS = {
  rad: ['rad', 'rada', 'radova'] as const,
  dan: ['dan', 'dana', 'dana'] as const,
  sat: ['sat', 'sata', 'sati'] as const,
  minut: ['minut', 'minuta', 'minuta'] as const,
  nedelja: ['nedelja', 'nedelje', 'nedelja'] as const,
  lekcija: ['lekcija', 'lekcije', 'lekcija'] as const,
  ostrvo: ['ostrvo', 'ostrva', 'ostrva'] as const,
  popravljen: ['popravljen', 'popravljena', 'popravljenih'] as const,
  clan: ['član', 'člana', 'članova'] as const,
  cvor: ['čvor', 'čvora', 'čvorova'] as const,
  greska: ['greška', 'greške', 'grešaka'] as const,
  upozorenje: ['upozorenje', 'upozorenja', 'upozorenja'] as const,
  izmena: ['izmena', 'izmene', 'izmena'] as const,
  mesec: ['mesec', 'meseca', 'meseci'] as const,
  lik: ['lik', 'lika', 'likova'] as const,
  ukras: ['ukras', 'ukrasa', 'ukrasa'] as const,
  polje: ['polje', 'polja', 'polja'] as const,
  poruka: ['poruka', 'poruke', 'poruka'] as const,
} satisfies Record<string, PluralForms>;

/**
 * Verb agreement after a counted subject. Serbian numerals 2-4 take a plural verb ("2 člana
 * imaju"), 1 and 5+ take singular — the same three-way split as the nouns, so the same
 * `pluralForm` index selects the right form. Participles also carry gender: these are
 * agreement triples for a masculine subject (član).
 */
export const VERBS = {
  ima: ['ima', 'imaju', 'ima'] as const,
  jeStajao: ['je već stajao', 'su već stajala', 'je već stajalo'] as const,
  jeOkacio: ['je već okačio', 'su već okačila', 'je već okačilo'] as const,
  nema: ['nema', 'nemaju', 'nema'] as const,
} satisfies Record<string, PluralForms>;

/**
 * Duration as a student would say it out loud. Estimates in this curriculum run from 3 minutes
 * to 5 hours, so the format has to hold both ends without looking silly at either.
 */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m === 0) return 'bez vremena';
  if (m < 60) return `${m} min`;

  const hours = Math.floor(m / 60);
  const rest = m % 60;

  if (rest === 0) return counted(hours, WORDS.sat);
  if (rest === 30) return hours === 1 ? 'sat i po' : `${hours},5 ${plural(hours, WORDS.sat)}`;
  if (hours === 1) return `${counted(hours, WORDS.sat)} i ${rest} min`;
  return `${counted(hours, WORDS.sat)} i ${rest} min`;
}

/** Rounded to something a person would actually plan around. */
export function formatMinutesRough(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m === 0) return formatMinutes(0);
  if (m < 60) return `${Math.max(5, Math.round(m / 5) * 5)} min`;
  const halves = Math.round(m / 30);
  return formatMinutes(halves * 30);
}

/** Serbian sentence case for a title that is stored in caps in the content file. */
export function sentenceCase(text: string): string {
  if (!text) return text;
  if (text !== text.toUpperCase()) return text;
  return text.charAt(0) + text.slice(1).toLowerCase();
}
